import { CompanyDraft, SelectData } from "../../../../../models";
import { SearchCollaboratorsResponse } from "../../../../../service/query";
import { ServiceError } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import {
  ActionButtons,
  Autocomplete,
  Column,
  ErrorBox,
  KanaTooltip,
  LoadingSpinner,
  Row,
  Select,
  TextArea,
  TextInput,
} from "../../../../../widget";
import { CompanyPanelLayout } from "./CompanyPanelLayout";
import { useAddEditCompanyModal } from "./useAddEditCompanyModal";

interface AddEditCompanyModalProps {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
  mode?: "add" | "edit";
  companyUuid?: string | null;
  fetchCompanyByUuid?: (companyUuid: string) => Promise<SearchCollaboratorsResponse["results"][number] | null>;
  onBackToDetails?: () => void;
  submitMode?: "api" | "deferred";
  onDeferredSave?: (draft: CompanyDraft) => void;
}

export const AddEditCompanyModal = ({
  show,
  onClose,
  onSaved,
  mode = "add",
  companyUuid = null,
  fetchCompanyByUuid,
  onBackToDetails,
  submitMode,
  onDeferredSave,
}: AddEditCompanyModalProps): JSX.Element => {
  const {
    formState,
    countryOptions,
    developerList,
    errors,
    isSaving,
    updateField,
    handleSubmit,
    handleCancel,
    onDeveloperSelect,
    onDeveloperTypeSearch,
    company,
    isCompanyLoading,
  } = useAddEditCompanyModal({
    onClose,
    onSaved,
    show,
    mode,
    companyUuid,
    fetchCompanyByUuid,
    submitMode,
    onDeferredSave,
  });
  const companyTypeOptions: SelectData = [
    { key: "Developer", value: "Developer" },
    { key: "Verifier", value: "Verifier" },
    { key: "Standard", value: "Standard" },
    { key: "Development Manager", value: "Development Manager" },
    { key: "Retail aggregator", value: "Retail aggregator" },
    { key: "Buyer", value: "Buyer" },
    { key: "Investor", value: "Investor" },
    { key: "Landowner", value: "Landowner" },
    { key: "Technology provider", value: "Technology provider" },
    { key: "Local authority", value: "Local authority" },
    { key: "Government", value: "Government" },
  ];
  const organisationOptions: SelectData = developerList;
  const headerTitle = mode === "edit" ? "Edit company" : "Add company";
  const titleText = mode === "edit" ? (company?.name ?? "Edit company") : "Add company";
  const showBackButton = mode === "edit" && onBackToDetails;

  if (mode === "edit" && !company && isCompanyLoading) {
    return (
      <CompanyPanelLayout
        show={show}
        onClose={handleCancel}
        header="Edit company"
        title="Loading company"
        subtitle="Company details"
        sectionTitle="Company details"
        showSectionHeader={false}
      >
        <Row spacingV="m" justify="center">
          <Column span={12} justify="center" align="center">
            <LoadingSpinner size="medium" type="dark" />
          </Column>
        </Row>
      </CompanyPanelLayout>
    );
  }

  return (
    <CompanyPanelLayout
      show={show}
      onClose={handleCancel}
      header={headerTitle}
      headerAction={
        showBackButton ? (
          <button type="button" className="BtnLink" onClick={onBackToDetails}>
            Back to details
          </button>
        ) : null
      }
      title={titleText}
      subtitle="Company details"
      sectionTitle="Company details"
      showTitleBlock={mode !== "add"}
      showSectionHeader={mode !== "add"}
    >
      <form className="AddEditCompanyModalForm" onSubmit={handleSubmit}>
        {errors.length > 0 && (
          <Row spacingV="m">
            <Column span={12}>
              <ErrorBox
                title="Please solve the below issues before saving"
                text={errors.map((error: ServiceError) => getErrorMessageFromCode(error.message))}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="m">
          <Column span={6}>
            <TextInput
              label="Name*"
              name="companyName"
              value={formState.name}
              onChange={(value) => updateField("name", value)}
              maxLength={100}
            />
          </Column>
          <Column span={6}>
            <Select
              label="Company type*"
              value={formState.collaboratorType}
              onChange={(value) => updateField("collaboratorType", value)}
              data={companyTypeOptions}
              placeholder="Select"
              corners="rounded"
            />
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={12}>
            <TextArea
              label="Description*"
              name="description"
              value={formState.description}
              onChange={(value) => updateField("description", value)}
              resizable
            />
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={6}>
            <TextInput
              label="Email address"
              name="emailAddress"
              type="email"
              value={formState.email}
              onChange={(value) => updateField("email", value)}
              optional
            />
          </Column>
          <Column span={6}>
            <TextInput
              label="Phone number"
              name="phoneNumber"
              value={formState.phone}
              onChange={(value) => updateField("phone", value)}
            />
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={6}>
            <TextInput
              label="Website"
              name="website"
              value={formState.website}
              onChange={(value) => updateField("website", value)}
              optional
            />
          </Column>
          <Column span={6}>
            <TextInput
              label="Address line 1"
              name="addressLine1"
              value={formState.addressLine1}
              onChange={(value) => updateField("addressLine1", value)}
              optional
            />
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={6}>
            <TextInput
              label="Address line 2"
              name="addressLine2"
              value={formState.addressLine2}
              onChange={(value) => updateField("addressLine2", value)}
              optional
            />
          </Column>
          <Column span={6}>
            <TextInput
              label="City"
              name="addressCity"
              value={formState.addressCity}
              onChange={(value) => updateField("addressCity", value)}
              optional
            />
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={6}>
            <TextInput
              label="Postcode"
              name="addressPostcode"
              value={formState.addressPostcode}
              onChange={(value) => updateField("addressPostcode", value)}
              optional
            />
          </Column>
          <Column span={6}>
            <TextInput
              label="County / Region / State"
              name="addressRegion"
              value={formState.addressRegion}
              onChange={(value) => updateField("addressRegion", value)}
              optional
            />
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={6}>
            <TextInput
              label="CRM ID"
              name="crmId"
              value={formState.crmId}
              onChange={(value) => updateField("crmId", value)}
              optional
            />
          </Column>
          <Column span={6}>
            <Select
              label="Country"
              value={formState.addressCountryCode}
              onChange={(value) => updateField("addressCountryCode", value)}
              data={countryOptions}
              placeholder="Select"
              corners="rounded"
            />
          </Column>
        </Row>
        {formState.collaboratorType === "Developer" && (
          <Row spacingV="m">
            <Column span={6}>
              <Autocomplete
                initialKey={formState.linkedOrganisationUuid}
                label="Link to existing Kana developer"
                placeholder="Select"
                data={organisationOptions}
                onSelect={onDeveloperSelect}
                onTypeSearch={onDeveloperTypeSearch}
                tooltip={
                  <KanaTooltip
                    tooltipHeader="Associations"
                    tooltipText="By linking this company to an existing developer organisation in the Kana Earth system, you can see all their associated projects/landholdings, rather than creating them."
                  />
                }
              />
            </Column>
          </Row>
        )}
        <Row spacingV="m">
          <Column span={6}>
            <ActionButtons submitButtonLabel="Save" onCancel={handleCancel} isLoading={isSaving} corners="rounded" />
          </Column>
        </Row>
      </form>
    </CompanyPanelLayout>
  );
};
